/**
 * Site-command dispatch outcome classifier.
 *
 * The unit-command endpoint (POST /odl-app/sites/<id>/unit/commands/) answers a
 * command batch with a two-array envelope { pending_commands, errors }: HTTP 200
 * queues everything (errors empty), 207 queues some (both arrays populated), and
 * an in-body 400 queues nothing (pending empty). This module turns that envelope
 * into a single discriminated outcome so command dialogs stop re-deriving the
 * success / partial / failed split by hand.
 *
 * Contains:
 * - DISPATCH_STATUS — the three outcome discriminants
 * - classifyDispatchEnvelope — envelope → { status, pendingCommands, failures }
 */

export const DISPATCH_STATUS = Object.freeze({
  SUCCESS: 'success',
  PARTIAL: 'partial',
  FAILED: 'failed',
});

/**
 * Classify a resolved dispatch envelope into a single outcome.
 *
 * @param {{ pending_commands?: object[], errors?: object[] }} [envelope]
 * @returns {{ status: string, pendingCommands: object[], failures: object[] }}
 *   pendingCommands = queued rows; failures = per-command error entries
 *   ({ index, message_key?, detail? }) carried by 207 / in-body 400.
 */
export function classifyDispatchEnvelope(envelope) {
  const pendingCommands = Array.isArray(envelope?.pending_commands) ? envelope.pending_commands : [];
  const failures = Array.isArray(envelope?.errors) ? envelope.errors : [];

  let status;
  if (failures.length === 0) {
    status = DISPATCH_STATUS.SUCCESS;
  } else if (pendingCommands.length === 0) {
    status = DISPATCH_STATUS.FAILED;
  } else {
    status = DISPATCH_STATUS.PARTIAL;
  }

  return { status, pendingCommands, failures };
}
